/**
 * The print root. Two boxes and nothing else: the 60 × 86 mm badge and the US-letter page.
 * `tools/proofs/pdf-pages.ts` proves a rendered PDF's page count and page box against these.
 */
import type { ReactNode } from 'react';

import { BADGE_MM, type PrintKind } from './sizes.js';

export { BADGE_MM, LETTER_IN, PT_PER_IN, PT_PER_MM, BADGE_PT, LETTER_PT } from './sizes.js';
export type { PrintKind } from './sizes.js';

export interface PrintRootProps {
  kind: PrintKind;
  /** One child per page. Each becomes one page; the proof counts them. */
  pages: readonly ReactNode[];
  title: string;
}

/**
 * Mount this alone on a print route. The `@page` size is set by kind; every child is one page
 * with a forced break after it. Screen CSS shows the boxes at their real size on a gray ground.
 */
export function PrintRoot({ kind, pages, title }: PrintRootProps): ReactNode {
  const size = kind === 'badge' ? `${BADGE_MM.width}mm ${BADGE_MM.height}mm` : 'letter';
  return (
    <div className={`ui-print ui-print--${kind}`} data-pages={pages.length} data-title={title}>
      <style>{`@page { size: ${size}; margin: 0; }`}</style>
      {pages.map((p, i) => (
        <section
          key={i}
          className="ui-print__page"
          aria-label={`${title} — page ${i + 1} of ${pages.length}`}
        >
          {/* The apple mark on every letter page, as the sign-in sheet's family carries it
              (James, Oct 1 — B8): the packet, the PO, the count sheet, the label and the receipt
              are one family on paper. The page's own eyebrow names the festival and the year. */}
          {kind === 'letter' ? (
            <img className="ui-print__mark" src="/favicon.png" width="28" height="28" alt="" />
          ) : null}
          {p}
        </section>
      ))}
    </div>
  );
}
